import { Sun, Umbrella, Zap, TrendingDown, Minimize2 } from "lucide-react";
import { FadeUp } from "./anim";
import { IMAGES } from "./site-data";

const Feature = ({ icon: Icon, title, body, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-sg-card p-7 sm:p-9 transition-colors duration-500 hover:border-sg-primary/40 ${className}`}
  >
    <span className="grid place-items-center w-11 h-11 rounded-full bg-sg-primary/12 text-sg-primary mb-6">
      <Icon size={20} strokeWidth={1.8} />
    </span>
    <h3 className="font-display text-2xl tracking-tight text-sg-fg">{title}</h3>
    <p className="mt-3 text-sm text-sg-muted font-light leading-relaxed max-w-sm">
      {body}
    </p>
  </div>
);

export const Features = () => {
  return (
    <section
      id="features"
      className="relative bg-sg-bg py-24 sm:py-32 border-t border-sg-border/50"
      data-testid="features-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <FadeUp className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-sg-primary">
            Engineered to last
          </span>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-sg-fg">
            Smart where it counts.
          </h2>
        </FadeUp>

        <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2 md:auto-rows-fr">
          <FadeUp className="md:col-span-2 md:row-span-2">
            <div className="relative h-full min-h-[320px] overflow-hidden rounded-2xl border border-white/5 group">
              <img
                src={IMAGES.product3}
                alt="Solar Garden across three settings"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sg-bg via-sg-bg/20 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 sm:p-10">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-sg-primary text-sg-bg mb-5">
                  <Sun size={20} strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-3xl sm:text-4xl tracking-tight text-sg-fg max-w-md">
                  An integrated solar canopy
                </h3>
                <p className="mt-3 text-sm sm:text-base text-sg-fg/70 font-light max-w-md">
                  The angled canopy isn't an add-on — it's the design. It shades
                  your garden and patio while a built-in microinverter turns the
                  sun into usable AC power. It's a cord-and-plug connected solar
                  PV system — no permanent hardwiring required.
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.05}>
            <Feature
              testid="feature-shade"
              className="h-full"
              icon={Umbrella}
              title="Adaptive shade"
              body="Protects delicate plants from scorching midday sun while you stay cool beside them."
            />
          </FadeUp>
          <FadeUp delay={0.1}>
            <Feature
              testid="feature-power"
              className="h-full"
              icon={Zap}
              title="Real AC power"
              body="A grid-tie microinverter converts solar into usable AC — the same power your home already runs on."
            />
          </FadeUp>
          <FadeUp delay={0.15}>
            <Feature
              testid="feature-savings"
              className="h-full"
              icon={TrendingDown}
              title="Offset your bill"
              body="Solar produced during the day can offset electricity you'd otherwise buy from the utility."
            />
          </FadeUp>
          <FadeUp delay={0.2}>
            <Feature
              testid="feature-footprint"
              className="h-full"
              icon={Minimize2}
              title="Small footprint"
              body="One compact freestanding feature — no giant array, no wasted backyard, no roof panels."
            />
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
